import React, { useState, useEffect } from "react";
import apiClient from "../api/apiClient";
import axios from "axios"; // Import axios directly
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";

const Projets = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);

  const fetchProjects = async () => {
    try {
      setLoading(true);
      setError(null);

      // Add timeout using axios directly
      const source = axios.CancelToken.source();
      const timeoutId = setTimeout(
        () => source.cancel("Request timeout"),
        10000,
      );

      const response = await apiClient.get(`/projets`, {
        cancelToken: source.token,
      });

      clearTimeout(timeoutId);

      // Validate response structure
      if (Array.isArray(response.data)) {
        setProjects(response.data);
      } else {
        throw new Error("Invalid data format received from server");
      }
    } catch (err) {
      if (axios.isCancel(err)) {
        // Use axios.isCancel directly
        setError(new Error("Request timeout: Please check your connection"));
      } else if (err.response) {
        setError(
          new Error(
            `Server Error: ${err.response.status} - ${
              err.response.data?.message || "Unknown error"
            }`,
          ),
        );
      } else if (err.request) {
        setError(
          new Error(
            "Network Error: Unable to reach the server. Please check your connection.",
          ),
        );
      } else {
        setError(new Error(`Error: ${err.message}`));
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProjects();
  }, [retryCount]);

  const handleRetry = () => {
    setRetryCount((prev) => prev + 1);
  };

  // Show full page loading
  if (loading && retryCount === 0) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: "100vh" }}
      >
        <div className="text-center">
          <div
            className="spinner-border text-primary"
            role="status"
            style={{ width: "3rem", height: "3rem" }}
          >
            <span className="visually-hidden">Chargement...</span>
          </div>
          <p className="mt-3">Chargement des projets...</p>
        </div>
      </div>
    );
  }

  // Show full page error if no projects data
  if (error && projects.length === 0) {
    return (
      <div className="container">
        <div className="row justify-content-center">
          <div className="col-lg-6">
            <div className="alert alert-danger mt-5" role="alert">
              <div className="text-center">
                <i className="fas fa-exclamation-triangle fa-3x mb-3 text-danger"></i>
                <h4>Erreur de chargement des projets</h4>
                <p className="mb-3">{error.message}</p>
                <button
                  className="btn btn-primary"
                  onClick={handleRetry}
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                      ></span>
                      Chargement...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-redo me-2"></i>
                      Réessayer
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // JSON-LD structured data for the list of projects
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: "Projets récents - DVELGE GROUP",
    url: "https://www.dvelgegroup.com/projets",
    numberOfItems: projects.length,
    itemListElement: projects.map((project, index) => ({
      "@type": "ListItem",
      position: index + 1,
      url: `https://dvelgegroup.com/projets/${project.id_projet}`,
      name: project.titre_projet,
      image: project.image_path || "images/portfolio/default.jpg",
      description: project.description || "",
    })),
  };

  return (
    <div>
      <Helmet>
        <title>Nos Projets | DVELGE GROUP</title>
        <meta
          name="description"
          content="Découvrez les projets récents de DVELGE GROUP, des solutions numériques innovantes et de qualité pour vos besoins digitaux."
        />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Nos Projets | DVELGE GROUP" />
        <meta
          property="og:description"
          content="Découvrez les projets récents de DVELGE GROUP, des solutions numériques innovantes et de qualité pour vos besoins digitaux."
        />
        <meta property="og:url" content="https://www.dvelgegroup.com/projets" />
        <meta property="og:image" content="images/portfolio/default.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Nos Projets | DVELGE GROUP" />
        <meta
          name="twitter:description"
          content="Découvrez les projets récents de DVELGE GROUP, des solutions numériques innovantes et de qualité pour vos besoins digitaux."
        />
        <meta name="twitter:image" content="images/portfolio/default.jpg" />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      {/* En-tête - This section doesn't depend on API data */}
      <section className="r-bg-x sec-pad">
        <div className="container">
          <div className="ree">
            <div className="row">
              <div className="col-lg-6 vcenter">
                <div className="cta-heading">
                  <h2 className="mb15">
                    Boostez votre entreprise avec{" "}
                    <span className="ree-text rt40">
                      des solutions numériques de classe mondiale
                    </span>
                  </h2>
                  <p>
                    Notre équipe multidisciplinaire de développeurs, ingénieurs
                    et designers livre des solutions innovantes, évolutives et
                    sécurisées — à temps et avec excellence.
                  </p>
                  <Link
                    to="/contacts"
                    className="ree-btn ree-btn-grdt1 mw-80 mt40"
                  >
                    Démarrez votre projet{" "}
                    <i className="fas fa-arrow-right fa-btn"></i>
                  </Link>

                  {/* Show warning if there was an error but we have cached data */}
                  {error && projects.length > 0 && (
                    <div className="alert alert-warning mt-3" role="alert">
                      <small>
                        <i className="fas fa-exclamation-triangle me-2"></i>
                        Données en cache: {error.message}
                        <button
                          className="btn btn-sm btn-outline-primary ms-2"
                          onClick={handleRetry}
                          disabled={loading}
                        >
                          {loading ? "Chargement..." : "Actualiser"}
                        </button>
                      </small>
                    </div>
                  )}
                </div>
              </div>
              <div className="col-lg-6 vcenter">
                <div className="ct-sol-img">
                  <img
                    src="images/cta/development.jpg"
                    alt="app mockup"
                    className="img-fluid"
                    onError={(e) => {
                      e.target.src = "/images/placeholder-project.svg";
                      e.target.alt = "Image non disponible";
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Présentation générale */}
      <div className="sec-pad r-bg-a">
        <div className="container">
          <div className="row">
            <div className="col-lg-5">
              <h2 className="mr50 mb15">Privilégier l'impact à l'abondance</h2>
              <p>Aperçu de nos récentes innovations</p>
            </div>
            <div className="col-lg-7 m-mt30">
              <h4 className="mb15">
                Retrouvez nos projets à travers l'écosystème numérique.
              </h4>
            </div>
          </div>

          {/* Loading state for projects section during retry */}
          {loading && retryCount > 0 ? (
            <div className="row justify-content-center mt70">
              <div className="col-12 text-center py-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Chargement...</span>
                </div>
                <p className="mt-3">Mise à jour des projets...</p>
              </div>
            </div>
          ) : (
            /* Grille des projets */
            <div className="row justify-content-center mt70">
              {projects.length > 0 ? (
                projects.map((project) => (
                  <div
                    className="col-lg-4 col-md-6 col-sm-6 mt30"
                    key={project.id_projet}
                  >
                    <div className="fwb-main-x fwb-a">
                      <div className="work-thumbnails">
                        <Link to={`/projets/${project.id_projet}`}>
                          <img
                            src={
                              project.image_path ||
                              "images/portfolio/default.jpg"
                            }
                            alt={project.titre_projet}
                            className="img-fluid"
                            onError={(e) => {
                              e.target.src = "/images/portfolio/default.jpg";
                              e.target.alt = "Image du projet non disponible";
                            }}
                          />
                        </Link>
                      </div>
                      <div className="work-details">
                        <p className="mb10">
                          {project.description || "Description non disponible"}
                        </p>
                        <h4>
                          <Link to={`/projets/${project.id_projet}`}>
                            {project.titre_projet}
                          </Link>
                        </h4>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="col-12 text-center py-5">
                  <div className="text-center">
                    <i className="fas fa-folder-open fa-3x text-muted mb-3"></i>
                    <h4 className="text-muted">Aucun projet disponible</h4>
                    <p className="text-muted mb-4">
                      Nous n'avons pas de projets à afficher pour le moment.
                    </p>
                    <button className="btn btn-primary" onClick={handleRetry}>
                      <i className="fas fa-redo me-2"></i>
                      Réessayer
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <section class="r-bg-x sec-pad">
        <div class="container">
          <div class="ree">
            <div class="row">
              <div class="col-lg-6 vcenter">
                <div class="cta-heading">
                  <h2 class="mb15">
                    Là où votre vision
                    <span class="ree-text rt40"> rencontre</span> sa
                    <span class="ree-text rt40">fondation</span>
                  </h2>
                  <p>
                    L'innovation est bien plus que de simples outils. C'est le
                    courage de bâtir l'avenir. DVELGE Group apporte la
                    profondeur technique et la maîtrise stratégique nécessaires
                    pour transformer vos idées les plus ambitieuses en une
                    réalité tangible.
                  </p>
                  <Link
                    to="/contacts"
                    class="ree-btn  ree-btn-grdt1 mw-80 mt40"
                  >
                    Lancez votre projet
                    <i class="fas fa-arrow-right fa-btn"></i>
                  </Link>
                </div>
              </div>
              <div class="col-lg-6 vcenter">
                <div class="ct-sol-img">
                  <img
                    src="images/cta/thinking.jpg"
                    alt="app mockup"
                    class="img-fluid"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Projets;
